import { Component } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@airtm/airtm-ui';
import { CHANSEY as CHANSEY_ERRORS } from '@airtm/utils/dist/errorCodes';
import { OTP_CODE_LENGTH } from '@constants';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import CodeModal from './CodeModal';
import PhoneModal from './PhoneModal';
import PhoneVerificationContainer from './PhoneVerificationContainer';

const I18N_ERRORS = {
  TOO_MANY_ATTEMPTS: 'SETTINGS:PHONE_TOO_MANY_ATTEMPTS',
  TOO_MANY_CONSECUTIVE_ATTEMPTS: 'SETTINGS:PHONE_TOO_MANY_CONSECUTIVE_ATTEMPTS',
  PHONE_IN_USE: 'SETTINGS:PHONE_IN_USE',
  MOBILE_CODE_EXPIRED: 'SETTING:PHONE_CODE_EXPIRED',
  UNEXPECTED_ERROR: 'SETTINGS:PHONE_UNEXPECTED_ERROR',
  INVALID_PHONE_NUMBER: 'SETTINGS:INVALID_PHONE_NUMBER',
  UNREACHABLE_PHONE_NUMBER: 'SETTINGS:INVALID_PHONE_UNREACHABLE',
};

// Common backdrop component ?
class PhoneVerification extends Component {
  static propTypes = {
    data: PropTypes.shape({
      me: PropTypes.shape({
        phone: PropTypes.string,
        phoneCountryCode: PropTypes.string,
        securityHub: PropTypes.shape({
          phoneValidated: PropTypes.bool,
        }),
      }),
      countries: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          phoneCode: PropTypes.string.isRequired,
        }),
      ),
    }).isRequired,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.shape({
      message: PropTypes.string,
    }),
    formCountry: PropTypes.string,
    formPhone: PropTypes.string,
    formPhoneCountryCode: PropTypes.string,
    formCountryPhoneRegex: PropTypes.string,
    closeOnFinish: PropTypes.bool,
    onChangeCallback: PropTypes.func,
    buttonText: PropTypes.string,
    modalTitle: PropTypes.string,
    onModalOpen: PropTypes.func,
    isSubmitting: PropTypes.bool,
    sendPhoneVerificationCode: PropTypes.func.isRequired,
    verifyPhone: PropTypes.func.isRequired,
    onVerifyAccount: PropTypes.func.isRequired,
    onlyPhone: PropTypes.bool,
    handleTouchVerification: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
  };
  modalList = [PhoneModal, CodeModal];

  constructor(props) {
    super(props);

    const {
      data,
      formCountry = '',
      formPhone = '',
      formPhoneCountryCode = '',
      formCountryPhoneRegex = '',
    } = props;

    this.state = {
      /**
       * `country` will be used in PhoneModal -> SelectCountryPhoneCode.
       * No need to retrieve it from data, this value will be retrieved and handled in
       * SelectCountryPhoneCode.
       */
      country: formCountry || '',
      currIndex: 0,
      errorMessage: null,
      errorTitle: '',
      method: '',
      showModal: false,
      otpCode: '',
      phoneCountryCode: data?.me?.phoneCountryCode || formPhoneCountryCode || '',
      countryPhoneRegex: formCountryPhoneRegex || '',
      phone: data?.me?.phone || formPhone || '',
    };
  }

  onCountryPhoneCodeChange = (phoneValues) => {
    const { onChangeCallback } = this.props;
    this.setState({ ...phoneValues }, () => {
      if (onChangeCallback) {
        onChangeCallback(phoneValues);
      }
    });
  };

  onChange = (field, value) => {
    const { onChangeCallback } = this.props;

    if (field === 'otpCode' && value.length === OTP_CODE_LENGTH) {
      this.setState({ [field]: value }, this.handleSubmitCode);
    } else {
      this.setState({ [field]: value }, () => {
        if (onChangeCallback) {
          onChangeCallback({ [field]: value });
        }
      });
    }
  };

  handleSendSms = () => {
    this.setState({ method: 'SMS' }, () => this.sendVerificationCode());
  };

  handleSendWhatsApp = () => {
    this.setState({ method: 'WHATSAPP' }, () => this.sendVerificationCode());
  };

  sendVerificationCode = async () => {
    const { sendPhoneVerificationCode } = this.props;
    const { method, phone, phoneCountryCode } = this.state;
    this.setState({ errorMessage: null, errorTitle: '' });
    try {
      await sendPhoneVerificationCode({
        phoneCountryCode,
        phone,
        method,
      });
      this.handleClickNext();
    } catch (error) {
      this.handleVerificationError(error);
    }
  };

  handleClickNext = () => {
    const { currIndex } = this.state;

    if (currIndex < this.modalList.length - 1) {
      this.setState({ currIndex: currIndex + 1 });
    } else {
      this.setState({ showModal: false });
    }
  };

  handleClickBack = () => {
    const { currIndex } = this.state;

    if (currIndex > 0) {
      this.setState({ currIndex: currIndex - 1 });
    } else {
      this.setState({ showModal: false });
    }
  };

  handleModalHide = () => {
    this.setState({ errorMessage: null, showModal: false, errorTitle: '' });
  };

  handleModalOpen = async () => {
    const {
      data,
      onVerifyAccount,
      onModalOpen = null,
      formPhone = '',
      formPhoneCountryCode = '',
      handleTouchVerification,
    } = this.props;

    const changingPhoneNumber =
      `${formPhoneCountryCode}${formPhone}` !== `${data?.me?.phoneCountryCode}${data?.me?.phone}`;

    if (data?.me?.securityHub?.phoneValidated && !changingPhoneNumber) {
      onVerifyAccount();
      return;
    }
    if (!onModalOpen || onModalOpen()) {
      handleTouchVerification(true);

      let newState = {
        showModal: true,
      };

      if (formPhone && formPhoneCountryCode) {
        newState = {
          ...newState,
          phone: formPhone,
          phoneCountryCode: formPhoneCountryCode,
        };
      }

      this.setState(newState);
    }
  };

  handleSubmitCode = async () => {
    const { verifyPhone, onVerifyAccount, closeOnFinish = false, t } = this.props;
    const { method, phone, phoneCountryCode, otpCode } = this.state;
    this.setState({ errorMessage: null, errorTitle: '' });
    const errorTitle = t('STATIC:SOMETHING_WENT_WRONG');
    try {
      const result = await verifyPhone({
        phoneCountryCode,
        phone,
        method,
        code: otpCode,
      });
      if (result.data?.verifyPhone?.phoneValidated) {
        onVerifyAccount();
        if (closeOnFinish) {
          this.setState({ showModal: false });
        }
      } else {
        this.setState({
          errorMessage: t('SETTINGS:VERIFICATION_PHONE_FAILED'),
          errorTitle,
        });
      }
    } catch (error) {
      let message = error?.message;
      if (CombinedGraphQLErrors.is(error)) {
        const [graphQLError] = error.errors;
        message = graphQLError?.message;
      }
      this.setState({ errorMessage: message, errorTitle });
    }
  };

  handleVerificationError = (error) => {
    const { t } = this.props;
    let errorTitle = t('STATIC:SOMETHING_WENT_WRONG');

    try {
      if (!CombinedGraphQLErrors.is(error)) {
        throw error;
      }

      const [graphQLError] = error.errors;
      const {
        extensions: {
          code,
          errorNumber,
          response: { status, body: { message, messages = {}, data = {} } } = {
            body: { message: '', messages: {} },
          },
        },
      } = graphQLError;
      let errorMessage;
      let errorData;

      if (status === 422) {
        const errors = [].concat(...Object.values(messages));
        [errorMessage] = errors;
        if (!errorMessage) {
          errorMessage = I18N_ERRORS.UNEXPECTED_ERROR;
        }
      } else {
        errorData = data;
        switch (errorNumber) {
          case CHANSEY_ERRORS.TOO_MANY_ATTEMPTS.code:
            errorTitle = t('STATIC:LIMIT_REACHED');
            errorMessage = I18N_ERRORS.TOO_MANY_ATTEMPTS;
            break;
          case CHANSEY_ERRORS.TOO_MANY_CONSECUTIVE_ATTEMPTS.code:
            errorMessage = I18N_ERRORS.TOO_MANY_CONSECUTIVE_ATTEMPTS;
            break;
          case CHANSEY_ERRORS.MOBILE_CODE_EXPIRED.code:
            errorMessage = I18N_ERRORS.MOBILE_CODE_EXPIRED;
            break;
          case CHANSEY_ERRORS.INVALID_PHONE_NUMBER.code:
            errorTitle = t('SETTINGS:INVALID_PHONE_NUMBER_TITLE');
            errorMessage = I18N_ERRORS.INVALID_PHONE_NUMBER;
            break;
          case CHANSEY_ERRORS.UNREACHABLE_PHONE_NUMBER.code:
            errorTitle = t('SETTINGS:INVALID_PHONE_UNREACHABLE_TITLE');
            errorMessage = I18N_ERRORS.UNREACHABLE_PHONE_NUMBER;
            break;
          default:
            break;
        }
      }

      if (status !== 422 && !errorMessage && code && code !== 'RATE_LIMIT_ERROR') {
        throw new Error(message);
      }

      this.setState({ errorMessage, errorData, errorTitle });
    } catch (unexpectedError) {
      /* eslint-disable-next-line no-console */
      console.error('Failed verifying phone', {
        namespace: PhoneVerification,
        message: unexpectedError,
      });
      this.setState({ errorMessage: I18N_ERRORS.UNEXPECTED_ERROR, errorTitle });
    }
  };

  render() {
    const {
      t,
      error,
      loading,
      onlyPhone = false,
      buttonText = '',
      isSubmitting = false,
      modalTitle = '',
    } = this.props;
    const {
      country,
      currIndex,
      errorMessage,
      errorData,
      phone,
      phoneCountryCode,
      countryPhoneRegex,
      showModal,
      otpCode,
      method,
      errorTitle,
    } = this.state;
    const ModalComponent = this.modalList[currIndex];

    if (loading) {
      return null;
    }

    if (error) {
      return <div>Error: {error.message}</div>;
    }

    let buttonLabel = onlyPhone
      ? t('SETTINGS:VERIFICATION_NOW_ONLY_PHONE')
      : t('SETTINGS:VERIFICATION_NOW');

    buttonLabel = buttonText || buttonLabel;

    return (
      <>
        <div className="text-center">
          <Button
            variant="primary"
            block
            data-testid="phone-verification-trigger"
            onClick={this.handleModalOpen}
            disabled={isSubmitting}
          >
            {buttonLabel}
          </Button>
        </div>

        <ModalComponent
          country={country}
          errorMessage={errorMessage && t(errorMessage, errorData)}
          errorTitle={errorTitle}
          modalTitle={modalTitle}
          onChange={this.onChange}
          onCountryPhoneCodeChange={this.onCountryPhoneCodeChange}
          onClickBack={this.handleClickBack}
          onClickNext={this.handleClickNext}
          onHide={this.handleModalHide}
          onSendSMS={this.handleSendSms}
          onSendWhatsApp={this.handleSendWhatsApp}
          onSubmitCode={this.handleSubmitCode}
          otpCode={otpCode}
          otpMethod={method}
          phone={phone}
          phoneCountryCode={phoneCountryCode}
          countryPhoneRegex={countryPhoneRegex}
          show={showModal}
          step={currIndex + 1}
        />
      </>
    );
  }
}

export default PhoneVerificationContainer(PhoneVerification);
